import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { getInitFormData, getNoSlip } from "@shared/utils/CommonUtils";
import { dateToString } from "@shared/utils/DateUtils";
import { global } from "@shared/libs/Global";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import SearchPanel from "@widgets/SearchPanel";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnButton from "@shared/components/SnButton";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import GrwDraftPopup, {
  ST_APPROVE,
  buildDraftBody,
  buildDraftTitle,
  type GrwDraftData,
} from "@widgets/commonpopup/GrwDraftPopup";
import {
  type GrwLineRow,
  type GrwPathGroup,
} from "@widgets/commonpopup/GrwApprovalLinePopup";

interface Props {
  Menuprops: menuInfo;
}

/** 경로그룹을 담아두는 테이블 (결재라인 템플릿) */
const PATH_GROUP_TABLE = "grw_line_template";

/** 결재문서 헤더 */
const APPROVE_TABLE = "grw_approveh";

/** 결재라인 — 상신할 때만 넣는다 (넣으면 백엔드가 상신으로 판단한다) */
const APPROVE_LINE_TABLE = "grw_approvel";

/** 이 화면의 저장 엔드포인트 — 경로그룹과 결재문서가 같이 탄다 */
const SAVE_ENDPOINT = "SN_GRW_BAS_REG_003_SAVE_01";

/**
 * 결재문서 채번 기준 — sys_notescounth 에 등록된 값을 그대로 쓴다.
 *
 *   select * from sys_notescounth where cd_firm = '1000' and cd_module = 'GRW'
 *   → GRW, no_line 9, cd_prefix APP, 결재문서
 *
 * 여기 숫자를 바꾸려면 테이블부터 확인할 것. no_line은 sys_notescount.no_line이다.
 */
const DRAFT_COUNTER = { cd_module: "GRW", no_line: 9, cd_prefix: "APP" };

/**
 * js_line(jsonb)을 배열로 되돌린다.
 *
 * 같은 값이 경로에 따라 배열 / JSON 문자열 / {type:'jsonb', value} / 이중 인코딩으로 온다.
 * 어느 쪽이든 결재경로 배열 하나로 맞춘다.
 */
const parseLines = (raw: any): GrwLineRow[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") {
    const inner = (raw as any).value;
    return typeof inner === "string" ? parseLines(inner) : [];
  }
  if (typeof raw !== "string") return [];

  let value: any = raw;
  for (let i = 0; i < 3; i++) {
    try {
      value = JSON.parse(typeof value === "string" ? value.trim() : value);
    } catch {
      return [];
    }
    if (Array.isArray(value)) return value;
    if (typeof value !== "string") return [];
  }
  return [];
};

// 양식조회
const SN_GRW_BAS_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const { userInfo } = global();

  /** 결재경로 팝업 왼쪽에 뿌릴 부서 목록 (mas_dept) */
  const [depts, setDepts] = useState<any[]>([]);

  /** 마지막으로 읽은 경로그룹 목록 — 새 템플릿번호를 매길 때 쓴다 */
  const pathGroupsRef = useRef<GrwPathGroup[]>([]);

  /**
   * 이번에 연 기안 문서의 키.
   * 기안작성에서 채번 + INSERT까지 끝낸 뒤 팝업이 닫힐 때까지 들고 있는다.
   */
  const draftKeyRef = useRef<{ no_approve: string; no_doc: string } | null>(
    null
  );

  /** 임시저장·상신이 한 번이라도 성공했는지 — 아니면 닫을 때 껍데기를 지운다 */
  const draftTouchedRef = useRef(false);

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  /**
   * 부서 목록을 미리 받아둔다 (결재경로 팝업 왼쪽).
   * SQL은 sys_query에 등록하고 화면은 쿼리 id만 부른다 — 이 프로젝트의 조회 규약이다.
   */
  useEffect(() => {
    let alive = true;
    const loadDepts = async () => {
      try {
        const data: any = await fnGetDataSet({
          id: "sn_grw_bas_reg_003_02",
          params: { cd_firm: userInfo.cd_firm, cd_biz: userInfo.cd_biz },
        });
        if (alive) setDepts(data?.[0] ?? []);
      } catch {
        /* 쿼리가 아직 없거나 실패하면 빈 목록으로 둔다 */
      }
    };
    loadDepts();
    return () => {
      alive = false;
    };
  }, [userInfo.cd_firm, userInfo.cd_biz]);

  /**
   * 결재경로 팝업의 사원 조회.
   *
   * 부서를 클릭하면 cd_dept만, 사원검색을 누르면 nm_emp만 채워서 들어온다.
   * 쿼리(sn_grw_bas_reg_003_03)가 양쪽 다 "빈 값이면 조건 무시"로 처리하므로
   * 안 쓰는 쪽은 빈 문자열로 넘긴다.
   */
  const searchEmp = async (cond: { cd_dept?: string; nm_emp?: string }) => {
    try {
      const data: any = await fnGetDataSet({
        id: "sn_grw_bas_reg_003_03",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_biz: userInfo.cd_biz,
          cd_dept: cond.cd_dept ?? "",
          nm_emp: cond.nm_emp ?? "",
        },
      });
      return data?.[0] ?? [];
    } catch {
      /* 쿼리가 아직 없거나 실패하면 빈 목록으로 둔다 */
      return [];
    }
  };

  /**
   * 경로그룹(결재라인 템플릿) 목록.
   *
   * 팝업이 열릴 때·추가·삭제 뒤에 직접 부른다. 새 템플릿번호(no_line)를 매길 때
   * 마지막으로 읽은 목록이 필요해서 ref에도 남겨둔다.
   */
  const loadPathGroups = async (): Promise<GrwPathGroup[]> => {
    try {
      const data: any = await fnGetDataSet({
        id: "sn_grw_bas_reg_003_04",
        params: { cd_firm: userInfo.cd_firm, cd_emp: userInfo.cd_emp },
      });
      const rows: any[] = data?.[0] ?? [];
      const groups = rows.map((r) => ({
        code: String(r.no_line ?? ""),
        name: r.nm_template ?? "",
        lines: parseLines(r.js_line),
      }));
      pathGroupsRef.current = groups;
      return groups;
    } catch {
      /* 쿼리가 아직 없거나 실패하면 빈 목록으로 둔다 */
      return [];
    }
  };

  /**
   * 경로그룹 추가.
   *
   * 템플릿은 사원별(PK: cd_firm + cd_emp + no_line)이라 번호도 그 사람 것 중에서 매긴다.
   * 결재경로는 js_line(jsonb)에 배열 그대로 넣는다.
   */
  const addPathGroup = async (nm_template: string, lines: GrwLineRow[]) => {
    const noLine =
      pathGroupsRef.current.reduce(
        (max, g) => Math.max(max, Number(g.code) || 0),
        0
      ) + 1;

    const ok = await fnSave(SAVE_ENDPOINT, {
      [PATH_GROUP_TABLE]: {
        insert: [
          {
            cd_firm: userInfo.cd_firm,
            cd_emp: userInfo.cd_emp,
            no_line: noLine,
            nm_template,
            yn_use: "Y",
            js_line: lines,
            cd_user_reg: userInfo.cd_user,
          },
        ],
        update: [],
        delete: [],
        modifiedRowcount: 1,
      },
    });

    return !!ok;
  };

  const deletePathGroup = async (cd_group: string) => {
    const confirm = await showDialog(
      "confirm",
      "선택한 경로그룹을 삭제하시겠습니까?"
    );
    if (!confirm) return false;

    const ok = await fnSave(SAVE_ENDPOINT, {
      [PATH_GROUP_TABLE]: {
        insert: [],
        update: [],
        delete: [
          {
            cd_firm: userInfo.cd_firm,
            cd_emp: userInfo.cd_emp,
            no_line: Number(cd_group),
          },
        ],
        modifiedRowcount: 1,
      },
    });

    return !!ok;
  };

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const onSearch = async () => {
    try {
      const params = {
        id: "sn_grw_bas_reg_003_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0] || [],
      });
    } catch (error: any) {}
  };

  /**
   * 지금 선택된 양식 행을 찾는다.
   *
   * focusedRowNode는 셀에 포커스가 들어가야 채워지므로(SnGrid.tsx:2011 handleCellFocused),
   * 행을 클릭하지 않고 체크만 했거나 조회 직후 상태에서는 비어 있다.
   * 그래서 포커스 → 선택행 → 행이 하나뿐이면 그 행 순으로 찾는다.
   */
  const getSelectedForm = () => {
    const grid = gridRefs.current["grid1"].current;
    if (!grid) return null;

    const focused = grid.focusedRowNode?.()?.data;
    if (focused) return focused;

    const selected = grid.getSelectedRows?.();
    if (Array.isArray(selected) && selected.length > 0) return selected[0];

    const rows: any[] = [];
    grid.forEachNode?.((node: any) => {
      if (node?.data) rows.push(node.data);
    });
    return rows.length === 1 ? rows[0] : null;
  };

  /**
   * 문서번호 채번.
   *
   * 발주등록(SN_PUR_POR_REG_001)이 쓰는 것과 같은 공통 채번(getNoSlip → com_CommonMapper_01)이고,
   * 기준값만 GRW 것으로 바꿔 부른다. 기준일자는 기안일(오늘)이다.
   */
  const getDraftNo = async (): Promise<string> => {
    try {
      const noSlip = await getNoSlip(
        Menuprops.cd_module || DRAFT_COUNTER.cd_module,
        DRAFT_COUNTER.no_line,
        DRAFT_COUNTER.cd_prefix,
        dateToString(new Date()),
        Menuprops.cd_menu
      );
      return typeof noSlip === "string" ? noSlip : "";
    } catch {
      return "";
    }
  };

  /**
   * grw_approveh 한 줄.
   *
   * st_approve와 tm_* 는 서버가 다시 박으므로 여기 값은 참고용이다.
   */
  const toApprovehRow = (
    no_approve: string,
    no_doc: string,
    st: string,
    no_form: string,
    nm_title: string,
    js_body: string
  ) => ({
    cd_firm: userInfo.cd_firm,
    cd_biz: userInfo.cd_biz,
    no_approve,
    no_doc,
    no_form,
    nm_title,
    cd_emp: userInfo.cd_emp,
    cd_dept: userInfo.cd_dept,
    st_approve: st,
    js_body,
    // TODO: 첨부는 아직 업로드 경로가 없어 빈 배열로 나간다 (draft.files 미사용)
    js_attach: [],
    js_comment: [],
    cd_user_reg: userInfo.cd_user,
    cd_user_amd: userInfo.cd_user,
  });

  /**
   * 기안작성 직후 — 01로 행을 만들어 둔다.
   *
   * 팝업이 열리자마자 보여줄 제목·본문을 그대로 넣는다. 팝업과 같은 함수로 만들기 때문에
   * 임시저장 없이 닫혀도(=DB에 남는 경우) 화면에서 본 내용과 어긋나지 않는다.
   */
  const insertDraft = async (
    no_approve: string,
    no_doc: string,
    form: any
  ) => {
    const ok = await fnSave(
      SAVE_ENDPOINT,
      {
        [APPROVE_TABLE]: {
          insert: [
            toApprovehRow(
              no_approve,
              no_doc,
              ST_APPROVE.DRAFT,
              form?.no_form ?? "",
              buildDraftTitle(form),
              buildDraftBody(form, no_doc)
            ),
          ],
          update: [],
          delete: [],
          modifiedRowcount: 1,
        },
      },
      undefined,
      // 문서를 열기만 한 것이라 저장 메시지는 띄우지 않는다
      { silentSuccess: true }
    );
    return !!ok;
  };

  /** 헤더 update 한 건 — 임시저장·상신이 같이 쓴다 */
  const toApprovehUpdate = (
    key: { no_approve: string; no_doc: string },
    draft: GrwDraftData
  ) => ({
    insert: [],
    update: [
      toApprovehRow(
        key.no_approve,
        key.no_doc,
        draft.st_approve,
        draft.no_form,
        draft.nm_title,
        draft.js_body
      ),
    ],
    delete: [],
    modifiedRowcount: 1,
  });

  /**
   * 임시저장 — 헤더만 보낸다.
   *
   * grw_approvel.insert가 실리면 백엔드가 상신으로 보고 헤더를 02로 올리므로
   * 임시저장에는 절대 넣지 않는다.
   */
  const updateDraft = async (draft: GrwDraftData) => {
    const key = draftKeyRef.current;
    if (!key) return false;

    const ok = await fnSave(SAVE_ENDPOINT, {
      [APPROVE_TABLE]: toApprovehUpdate(key, draft),
    });

    if (ok) draftTouchedRef.current = true;
    return !!ok;
  };

  /** 상신 — 헤더 update와 결재라인 insert를 한 번에 보낸다 */
  const submitDraft = async (draft: GrwDraftData) => {
    const key = draftKeyRef.current;
    if (!key) return false;

    const ok = await fnSave(SAVE_ENDPOINT, {
      [APPROVE_TABLE]: toApprovehUpdate(key, draft),
      [APPROVE_LINE_TABLE]: {
        // cd_firm·no_approve는 헤더와 같은 값이어야 한다
        insert: draft.approvalLines.map((line) => ({
          cd_firm: userInfo.cd_firm,
          no_approve: key.no_approve,
          ...line,
        })),
        update: [],
        delete: [],
        modifiedRowcount: draft.approvalLines.length,
      },
    });

    if (ok) draftTouchedRef.current = true;
    return !!ok;
  };

  /** 아무것도 저장하지 않고 닫았으면 껍데기 행을 지운다 */
  const discardEmptyDraft = async () => {
    const key = draftKeyRef.current;
    draftKeyRef.current = null;
    if (!key || draftTouchedRef.current) return;

    await fnSave(
      SAVE_ENDPOINT,
      {
        [APPROVE_TABLE]: {
          insert: [],
          update: [],
          delete: [{ cd_firm: userInfo.cd_firm, no_approve: key.no_approve }],
          modifiedRowcount: 1,
        },
      },
      undefined,
      { silentSuccess: true }
    );
  };

  /**
   * 기안작성 — 문서번호를 채번하고 결재문서를 01로 만든 뒤 문서기안 팝업을 띄운다.
   * 선택한 행이 있으면 그 양식으로 채우고, 없으면 빈 상태로 연다.
   */
  const onDraftWrite = async () => {
    const row = getSelectedForm();

    // 번호 없이 기안하면 본문 문서번호가 빈 채로 남으므로 여기서 막는다
    const noDoc = await getDraftNo();
    if (!noDoc) {
      await showDialog("error", "문서번호 채번에 실패했습니다.");
      return;
    }

    // no_approve는 채번한 문서번호를 그대로 쓴다.
    // getNoSlip이 서버 채번이라 동시에 눌러도 겹치지 않는다.
    const noApprove = noDoc;

    // 여는 순간 01(기안)로 잡아둔다. 임시저장·상신은 이 행을 UPDATE 한다.
    if (!(await insertDraft(noApprove, noDoc, row))) return;
    draftKeyRef.current = { no_approve: noApprove, no_doc: noDoc };
    draftTouchedRef.current = false;

    await showPopupModal({
      title: "문서기안",
      children: (
        <GrwDraftPopup
          form={row}
          noDoc={noDoc}
          // 저장/상신/선택 API는 화면에서 붙인다 (팝업은 API를 부르지 않는다)
          onTempSave={(draft) => {
            void updateDraft(draft);
          }}
          // 저장 결과를 돌려줘야 팝업이 성공했을 때만 닫는다
          onSubmit={(draft) => submitDraft(draft)}
          onApprovalLine={() => {
            // TODO: 결재라인 선택 팝업
          }}
          // onApprovalLineAdd를 넘기지 않으면 팝업이 기본 결재경로 창을 띄운다.
          // 부서/사원은 이 화면에서 조회해 넘긴다.
          depts={depts}
          // 경로그룹은 팝업이 열릴 때 직접 읽는다 (여기 state를 넘기면 추가해도 안 바뀐다)
          onLoadPathGroups={loadPathGroups}
          onAddPathGroup={addPathGroup}
          onDeletePathGroup={deletePathGroup}
          // 이 화면은 결재·합의·수신처만 쓴다 (공람/시행자 미사용)
          approvalLineTabs={["결재", "합의", "수신처"]}
          onSearchEmp={searchEmp}
          onReceiver={() => {
            // TODO: 수신 선택 팝업
          }}
          onAttach={() => {
            // TODO: 첨부 선택 (Drag&Drop 영역 클릭으로도 선택된다)
          }}
        />
      ),
      // 좌측 본문 + 우측 결재/수신/첨부가 함께 들어가는 화면이라 넓게 잡는다
      width: "1240px",
      height: "800px",
      okayBtn: false,
    });

    // 팝업이 닫힌 뒤 — 한 번도 저장하지 않았으면 껍데기를 정리한다
    await discardEmptyDraft();
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {},
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      {/*
        기안작성 버튼은 검색조건 줄 오른쪽 끝에 붙인다.
        SearchPanel 안(sys_formctrl)에 넣으면 컨트롤이 왼쪽부터 순서대로 흘러가는 구조라
        오른쪽 끝으로 못 민다. 그래서 검색조건과 버튼을 한 줄로 감싸 이 화면에서만 배치한다.
      */}
      <div className="mb-1 flex items-start">
        <div className="flex-1 min-w-0">
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
          ></SearchPanel>
        </div>
        <div className="shrink-0 pt-2 pr-2">
          <SnButton text="기안작성" onClick={onDraftWrite} />
        </div>
      </div>

      <div className="flex-1 flex flex-col gap-1.5 min-h-0">
        <div className="flex-1 min-h-200">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single" }}
            events={grids.grid1.events}
          />
        </div>
      </div>
    </div>
  );
});

export default SN_GRW_BAS_REG_003;
